import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';

interface ProgressBarProps {
  value: number; // 0-100
  threshold?: number; // Umbral para cambiar color (default: 85)
  label?: string;
  showPercentage?: boolean;
  height?: 'sm' | 'md' | 'lg';
  showThresholdLine?: boolean;
}

export default function ProgressBar({
  value,
  threshold = 85,
  label,
  showPercentage = true,
  height = 'md',
  showThresholdLine = true,
}: ProgressBarProps) {
  const clampedValue = Math.min(Math.max(value, 0), 100);
  const isPassing = clampedValue >= threshold;

  const heightClasses = {
    sm: 'h-2',
    md: 'h-4',
    lg: 'h-6',
  };

  const getBarColor = () => {
    if (clampedValue >= threshold) {
      return 'bg-green-500';
    } else if (clampedValue >= threshold * 0.7) {
      return 'bg-yellow-500';
    } else if (clampedValue >= threshold * 0.5) {
      return 'bg-orange-500';
    } else {
      return 'bg-red-500';
    }
  };

  return (
    <div className="w-full">
      {label && (
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">{label}</span>
          {showPercentage && (
            <span className={`text-sm font-bold ${isPassing ? 'text-green-600' : 'text-destructive'}`}>
              {clampedValue.toFixed(1)}%
            </span>
          )}
        </div>
      )}

      <div className="relative w-full">
        {/* Background */}
        <div className={`w-full ${heightClasses[height]} bg-secondary rounded-full overflow-hidden`}>
          {/* Progress bar */}
          <div
            className={`${heightClasses[height]} ${getBarColor()} rounded-full transition-all duration-500 ease-out relative`}
            style={{ width: `${clampedValue}%` }}
          >
          </div>
        </div>

        {/* Threshold line */}
        {showThresholdLine && (
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-border"
            style={{ left: `${threshold}%` }}
            title={`Umbral: ${threshold}%`}
          >
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-border rounded-full"></div>
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-border rounded-full"></div>
          </div>
        )}
      </div>

      {/* Status indicator */}
      <div className="mt-2 flex items-center gap-2">
        {isPassing ? (
          <>
            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 dark:bg-green-900/30 dark:text-green-400 px-2 py-1 rounded-full border border-green-200 dark:border-green-800">
              <FontAwesomeIcon icon={faCheckCircle} /> Cumple el criterio B2 (≥{threshold}%)
            </span>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-1 text-xs font-medium text-destructive bg-destructive/10 px-2 py-1 rounded-full border border-destructive/20">
              <FontAwesomeIcon icon={faTimesCircle} /> No cumple el criterio B2 (&lt;{threshold}%)
            </span>
            <span className="text-xs text-muted-foreground">
              Necesitas {(threshold - clampedValue).toFixed(1)}% más
            </span>
          </>
        )}
      </div>
    </div>
  );
}

export { ProgressBar };
